import { fromJS, Map, List } from 'immutable'
import isFunction from 'lodash/isFunction'
import {
  PLAN_SKU_SAGE_SPECIAL_ACCESS,
  PLAN_SKU_ONE_DOLLAR_THIRTY_DAYS_TO_MONTHLY,
  PLAN_SKU_ONE_MONTH_TRIAL_TO_ANNUAL,
  PLAN_SUBSCRIPTION_MONTHLY,
  PLAN_SUBSCRIPTION_ANNUAL,
  PLAN_SUBSCRIPTION_LIVE,
  formatPriceUnaltered,
  findPlanInDataList,
  isFourteenMonthPlan,
} from '../../services/plans/index'
import { USD, LTM } from '../../services/currency/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { historyRedirect } from '../../services/navigation/index'
import {
  URL_CART_BILLING,
} from '../../services/url/constants'
import { TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM } from '../../services/dialog/index'

export function getPlanCardData (options) {
  const {
    plans,
    currencyIso,
    staticText,
    userLanguage,
  } = options
  const plansHaveFreeTrial = !!plans.find((p) => parseInt(p.get('costs').first(), 10) === 0)

  const mapPlanFeatureToData = (planType, isFourteenMonth = false, sku, price) => {
    if (
      sku === PLAN_SKU_SAGE_SPECIAL_ACCESS
        || sku === PLAN_SKU_ONE_MONTH_TRIAL_TO_ANNUAL
        || sku === PLAN_SKU_ONE_DOLLAR_THIRTY_DAYS_TO_MONTHLY
    ) {
      return {
        feature: `${staticText.get('specialAccess')} ${formatPriceUnaltered(currencyIso, price, userLanguage)}`,
      }
    }
    switch (planType) {
      case PLAN_SUBSCRIPTION_MONTHLY:
        return {
          feature: staticText.get('featureTrial'),
        }
      case PLAN_SUBSCRIPTION_ANNUAL:
        return {
          feature: (isFourteenMonth
            ? staticText.get('blackFridayFeatureTrial')
            : staticText.get('featureTrial')),
        }
      case PLAN_SUBSCRIPTION_LIVE:
        return {
          feature: staticText.get('featurePremium'),
        }
      default:
        return {}
    }
  }

  return plans.map((plan, index) => {
    const planType = plan.get('legacyType')
    const initialCost = plan.get('costs').first()
    const cost = plan.get('costs').last()
    const price = formatPriceUnaltered(currencyIso, cost, userLanguage) || cost
    const promoValue1 = plan.get('promoValue1')
    const promoValue2 = plan.get('promoValue2')
    // AP-16214 Black Friday Checkout - 2024
    const isFourteenMonth = isFourteenMonthPlan(plan.get('sku'))
    const annualPeriod = isFourteenMonth ? 14 : 12
    const monthlyCost = planType === PLAN_SUBSCRIPTION_MONTHLY
      ? price
      : formatPriceUnaltered(
        currencyIso,
        (parseFloat(cost) / annualPeriod).toFixed(2),
        userLanguage,
      )
    const monthlyCostUnformatted = planType === PLAN_SUBSCRIPTION_MONTHLY
      ? cost
      : (parseFloat(cost) / annualPeriod).toFixed(2)
    const freeTrial = parseInt(initialCost, 10) === 0
    const planData = ({
      title: isFourteenMonth ? staticText.get('annual') : plan.get('heading'),
      billed: plan.get('billingSchedule'),
      billedAbb: plan.get('billingPeriodAbbreviation'),
      type: planType,
      compositeType: plan.get('compositeType'),
      sku: plan.get('sku'),
      cost,
      price,
      monthlyCost,
      monthlyCostUnformatted,
      weeklyCostUnformatted: String(plan.get('pricePerWeek')),
      freeTrial,
      initialCost,
      bestValue: plan.get('bestValue'),
      percentSavings: plan.get('percentSavings'),
      valueProps: plan.get('valueProps'),
      currencyIso: plan.get('currencyIso', currencyIso),
      ...(
        (plansHaveFreeTrial)
          && mapPlanFeatureToData(planType, isFourteenMonth, plan.get('sku'), initialCost)
      ),
      providerFirstSegmentId: plan.get('providerFirstSegmentId'),
      providerSecondSegmentId: plan.get('providerSecondSegmentId'),
      promoValue1,
      promoValue2,
      index,
    })

    return fromJS(planData)
  })
}

export function getCurrencyIso (options) {
  const { userAccount = Map(), plansList = List(), selectedPlan = Map() } = options

  if (!Map.isMap(userAccount) || !List.isList(plansList)) {
    return null
  }

  const userAccountSubscriptions = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()) || Map()
  const currencyIso = userAccountSubscriptions.get('currencyIso')
    || selectedPlan.get('currencyIso')
    || (plansList.size > 0 && Map.isMap(plansList.get(0))) ? plansList.getIn([0, 'currencyIso']) : undefined
    || USD

  return currencyIso
}

/**
 * Function to run when the user clicks the cta link after choosing their plan
 * @param {Map} plan The selected plan
 * @param {String} currencyIso The currency ISO
 * @param {Object} props The full props
 */
export async function onChoosePlanClick (plan = Map(), currencyIso, props, sku) {
  const {
    history,
    userAccount = Map(),
    user = Map(),
    auth = Map(),
    setPlansLocalizedSelection,
    setPlansSelection,
    setPlanSelection,
    isCheckoutChangePlan,
    isAccountChangePlan,
    dismissModal,
    renderModal,
    setPlanChangeSelected,
    plans,
    userPlanSubscriptionType,
    cancellationScheduled,
    useGraphQLData,
    plansList = List(),
    currentPlan = Map(),
    sliderMode = false,
    onPlanSelect,
  } = props
  const language = user.getIn(['data', 'language'])
  const latamPricing = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'latamPricing'])
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const iso = isLoggedIn ? currencyIso : null
  const currency = latamPricing ? LTM : iso
  const hasOnPlanSelect = onPlanSelect && isFunction(onPlanSelect)

  if (!isLoggedIn && sliderMode && hasOnPlanSelect) {
    onPlanSelect()
    return
  }

  // Checkout and checkout change plan
  if (!isAccountChangePlan) {
    try {
      // Attempt to find the selected plan in the store first
      // If it is not found fire off the setPlansLocalizedSelection process
      const graphQLPlansList = List.isList(plansList) ? plansList : List()
      const selectedPlan = useGraphQLData ? graphQLPlansList.find((p) => p.get('sku') === sku) : findPlanInDataList(plans, plan.get('sku'))

      if (selectedPlan) {
        // set the selection in plans
        setPlansSelection(selectedPlan)
      }

      if (!useGraphQLData && !selectedPlan) {
        await setPlansLocalizedSelection({ plan, language, currency })
      }

      // set the plan in checkout
      setPlanSelection({ type: plan.get('type'), sku: plan.get('sku') })

      // Single Page Checkout - disable redirect
      if (!sliderMode) {
        if (!isCheckoutChangePlan) {
          historyRedirect({
            history, url: URL_CART_BILLING, auth, language,
          })
        } else {
          dismissModal()
        }
      }

      if (hasOnPlanSelect) {
        onPlanSelect()
      }
    } catch (error) {
      // Do Nothing for now
    }
  }

  // Account change plan
  if (isAccountChangePlan) {
    const planType = plan.get('type')
    const userPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
    const reactivateCurrentPlan = planType === userPlanSubscriptionType
      && cancellationScheduled

    if (!useGraphQLData) {
      setPlanChangeSelected(reactivateCurrentPlan ? userPlanSku : sku)
    }

    if (useGraphQLData && Map.isMap(currentPlan) && currentPlan.size) {
      const graphQLPlansList = List.isList(plansList) ? plansList : List()
      const selectedPlan = graphQLPlansList.find((p) => p.get('sku') === sku) || (sku === currentPlan.get('sku') && currentPlan) || Map()

      setPlanChangeSelected(selectedPlan)
    }

    renderModal(TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM, {
      hideDismiss: false,
      ...(useGraphQLData && { currentPlan }),
    })
  }
}
